import { isString } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { animated, useSpring } from '@react-spring/web';
import { hooks } from '@wove/react';
import { cloneElement, Fragment, ReactElement, useRef } from 'react';
import { CheckListItem } from '../check-list';
import { ArrowIcon } from '../icon';
import { CommonPropsWithChildren } from '../utils/native-props';

export type CustomTitleProps = {
  checked: boolean;
};

export type CollapseItemProps = {
  value: string | number;
  // 当title为React Dom时，会接收到checked属性
  title: string | ReactElement;
  disabled?: boolean;
  hideArrow?: boolean;
} & CommonPropsWithChildren;

const CollapseItemContent = (props) => {
  const classPrefix = `v-collapse-item`;
  const innerRef = useRef<HTMLDivElement>(null);
  const checked = props.checked;

  const onRest = hooks.useCallbackRef(() => {
    if (checked) {
      setTimeout(() => {
        api.start({ height: 'auto' });
      }, 200);
    }
  });

  const [{ height }, api] = useSpring(() => {
    return {
      from: { height: 0 as string | number },
      config: { mass: 1, tension: 280, friction: 60, duration: 100 },
      onRest: onRest,
    };
  });

  hooks.useUpdateEffect(() => {
    const offsetHeight = innerRef?.current?.offsetHeight;
    if (checked) {
      api.start({ height: offsetHeight });
    } else {
      api.start({ height: offsetHeight, immediate: true });
      setTimeout(() => {
        api.start({ height: 0 });
      }, 50);
    }
  }, [checked]);

  return (
    <div className={classNames(`${classPrefix}`, props.className as string)} style={props.style}>
      <div className={`${classPrefix}-title`} onClick={props.onClick}>
        <Fragment>
          {isString(props.title) ? (
            <div className={`${classPrefix}-title-text`}>{props.title}</div>
          ) : (
            <div className={`${classPrefix}-title-text`}>{cloneElement(props.title, { checked })}</div>
          )}
          {!props.hideArrow ? <ArrowIcon direction={checked ? 'top' : 'bottom'} /> : null}
        </Fragment>
      </div>
      <animated.div className={`${classPrefix}-content`} style={{ height: height }}>
        <div className={`${classPrefix}-content-inner`} ref={innerRef}>
          {props.content}
        </div>
      </animated.div>
    </div>
  );
};

/**
 * CollapseItem
 * ```
 * 1. 当属性title为React Dom时，会接收到checked属性
 * ```
 */
export const CollapseItem = (props: CollapseItemProps) => {
  return (
    <CheckListItem value={props.value} disabled={props.disabled}>
      <CollapseItemContent
        title={props.title}
        content={props.children}
        disabled={props.disabled}
        className={props.className}
        hideArrow={props.hideArrow}
        style={props.style}
      />
    </CheckListItem>
  );
};
